/**
 * Role-based access control — single source of truth.
 *
 * Add a role:        extend USER_ROLES + add key to ROLE_PERMISSIONS + add Czech label to ROLE_LABELS_CS.
 * Add a permission:  extend PERMISSIONS + grant to the roles that should have it.
 *
 * Permissions are strings of the form `{domain}.{action}` so grep finds call sites.
 * Ownership checks are separate (see packages/shared/src/lib/authorize.ts).
 */

export const USER_ROLES = ["admin", "ops", "obchodnik", "lektor", "asistent", "user"] as const;
export type UserRole = (typeof USER_ROLES)[number];

/**
 * Czech UI labels (with diacritics) for each role. Consumed by admin UI
 * (impersonation banner, users table, role dropdown) to render human-friendly
 * Czech text. Keep in sync with USER_ROLES.
 */
export const ROLE_LABELS_CS: Record<UserRole, string> = {
  admin: "Administrátor",
  ops: "Provoz",
  obchodnik: "Obchodník",
  lektor: "Lektor",
  asistent: "Asistent",
  user: "Uživatel",
};

export const PERMISSIONS = [
  // Admin surface
  "admin.panel.access",
  "users.list",
  "users.create",
  "users.update_role",
  "users.approve",
  "users.deactivate",
  "users.reactivate",
  "audit.read",
  "notifications.broadcast",
  // Provozní přepínače (Administrace → Nástroje) — např. kill-switch
  // klientských e-mailů. Záměrně jen pro admina: dopad je celoplošný.
  "settings.write",

  // Content moderation — admin-level overrides of ownership
  "notes.list_any",
  "notes.delete_any",
  "tasks.list_any",
  "tasks.delete_any",

  // Regular user surface — every authenticated, approved user gets these
  "notes.manage_own",
  "tasks.manage_own",
  "notifications.read_own",

  // HCA Moravia domain — read / write per module. Matrix in plan §C.3 / §S.3.
  "hca.dashboard.read_ops",
  "hca.dashboard.read_finance",
  "hca.products.read",
  "hca.products.write",
  "hca.terminy.read",
  "hca.terminy.write",
  "hca.prihlasky.read",
  "hca.prihlasky.write",
  "hca.prihlasky.sleva",
  "hca.crm.read",
  "hca.crm.write",
  "hca.faktury.read",
  "hca.faktury.write",
  "hca.dochazka.read",
  "hca.dochazka.write",
  "hca.pozorovani.read",
  "hca.pozorovani.write",
  "hca.feedback.read",
  "hca.op.read",
  "hca.op.write",
  "hca.provize.read_own",
  "hca.provize.read_all",
  "hca.email_serialy.write",
  "hca.lead_magnet.read",
  "hca.admin.write",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<UserRole, ReadonlySet<Permission>> = {
  // admin gets everything — both template and HCA surface
  admin: new Set<Permission>(PERMISSIONS),

  // ops (Míša) — day-to-day operations: terminy, prihlášky, docházka, email seriály, lead magnet
  ops: new Set<Permission>([
    "admin.panel.access",
    "notifications.broadcast",
    "notifications.read_own",
    "notes.manage_own",
    "tasks.manage_own",
    "hca.dashboard.read_ops",
    "hca.products.read",
    "hca.products.write",
    "hca.terminy.read",
    "hca.terminy.write",
    "hca.prihlasky.read",
    "hca.prihlasky.write",
    "hca.crm.read",
    "hca.faktury.read",
    "hca.dochazka.read",
    "hca.dochazka.write",
    "hca.pozorovani.read",
    "hca.feedback.read",
    "hca.op.read",
    "hca.email_serialy.write",
    "hca.lead_magnet.read",
  ]),

  // obchodník — CRM, pipeline, přihlášky, vlastní provize (OWN scoped in routers)
  obchodnik: new Set<Permission>([
    "notifications.read_own",
    "notes.manage_own",
    "tasks.manage_own",
    "hca.products.read",
    "hca.terminy.read",
    "hca.prihlasky.read",
    "hca.prihlasky.write",
    "hca.crm.read",
    "hca.crm.write",
    "hca.pozorovani.read",
    "hca.feedback.read",
    "hca.op.read",
    "hca.op.write",
    "hca.provize.read_own",
  ]),

  // lektor — read-only calendar + feedback přehled
  lektor: new Set<Permission>([
    "notifications.read_own",
    "notes.manage_own",
    "tasks.manage_own",
    "hca.products.read",
    "hca.terminy.read",
    "hca.prihlasky.read",
    "hca.dochazka.read",
    "hca.pozorovani.read",
    "hca.feedback.read",
  ]),

  // asistent — docházka a pozorování (write), read-only zbytek
  asistent: new Set<Permission>([
    "notifications.read_own",
    "notes.manage_own",
    "tasks.manage_own",
    "hca.products.read",
    "hca.terminy.read",
    "hca.prihlasky.read",
    "hca.dochazka.read",
    "hca.dochazka.write",
    "hca.pozorovani.read",
    "hca.pozorovani.write",
  ]),

  // user — minimal default role (public klient login, Phase 3 klub portal)
  user: new Set<Permission>(["notes.manage_own", "tasks.manage_own", "notifications.read_own"]),
};

/** Resolve the full permission set for a role. O(1). */
export function getPermissions(role: UserRole): ReadonlySet<Permission> {
  return ROLE_PERMISSIONS[role];
}

/**
 * Sjednocení oprávnění přes několik rolí (primární + doplňkové). Uživatel
 * s více rolemi má součet jejich oprávnění.
 */
export function getPermissionsForRoles(roles: readonly UserRole[]): ReadonlySet<Permission> {
  const out = new Set<Permission>();
  for (const role of roles) {
    for (const perm of ROLE_PERMISSIONS[role]) out.add(perm);
  }
  return out;
}

/** Does `role` grant `perm`? O(1) set lookup. */
export function hasPermission(role: UserRole, perm: Permission): boolean {
  return ROLE_PERMISSIONS[role].has(perm);
}

/** Narrow-typed Zod-friendly role guard — accepts unknown string, returns typed role or null. */
export function asUserRole(value: unknown): UserRole | null {
  return typeof value === "string" && (USER_ROLES as readonly string[]).includes(value)
    ? (value as UserRole)
    : null;
}
